import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getFeatureLinks, 
  createFeatureLink, 
  updateFeatureLink, 
  deleteFeatureLink,
  getFeatureFeatureLinks,
  createFeatureFeatureLink,
  deleteFeatureFeatureLink,
  searchFeaturesForLinking
} from '../services/featureService';
import { sanitizeInput, sanitizeURL } from '../utils/sanitize';
import { logger, handleError } from '../utils/logger';
import './RelatedInformation.css';

// External link types (non-Feature)
const EXTERNAL_LINK_TYPES = ['Internal Doc', 'Customer Doc', 'Dev Doc', 'Idea', 'Initiative'];

// All link types including Feature
const ALL_LINK_TYPES = ['Feature', ...EXTERNAL_LINK_TYPES];

// Feature link relationship types
const FEATURE_LINK_TYPES = [
  { value: 'depends_on', label: 'Depends on' },
  { value: 'depended_on_by', label: 'Depended on by' },
  { value: 'related_to', label: 'Related to' }
];

const getLinkTypeLabel = (linkType) => {
  const found = FEATURE_LINK_TYPES.find(t => t.value === linkType);
  return found ? found.label : linkType;
};

const RelatedInformation = ({ featureId, featureGroupId, isEditMode, onLinksChange }) => {
  const navigate = useNavigate();
  const [externalLinks, setExternalLinks] = useState([]);
  const [featureLinks, setFeatureLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLink, setEditingLink] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ 
    type: 'Internal Doc', 
    name: '', 
    url: '',
    targetFeatureId: '',
    featureLinkType: 'related_to'
  });
  const [errors, setErrors] = useState({});
  
  // Feature search state
  const [featureSearchQuery, setFeatureSearchQuery] = useState('');
  const [featureSearchResults, setFeatureSearchResults] = useState([]);
  const [showFeatureDropdown, setShowFeatureDropdown] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);

  const loadAllLinks = useCallback(async () => {
    try {
      setLoading(true);
      const [external, features] = await Promise.all([
        getFeatureLinks(featureId),
        getFeatureFeatureLinks(featureId)
      ]);
      setExternalLinks(external);
      setFeatureLinks(features);
      if (onLinksChange) {
        onLinksChange({ external, features });
      }
    } catch (error) {
      logger.error('Error loading links:', error);
    } finally {
      setLoading(false);
    }
  }, [featureId, onLinksChange]);

  useEffect(() => {
    if (featureId) {
      loadAllLinks();
    } else {
      setExternalLinks([]);
      setFeatureLinks([]);
      setLoading(false);
    }
  }, [featureId, loadAllLinks]);

  // Search features as user types
  useEffect(() => {
    const searchFeatures = async () => {
      if (featureSearchQuery.trim().length < 2 || !featureGroupId) {
        setFeatureSearchResults([]);
        return;
      }
      try {
        const results = await searchFeaturesForLinking(featureSearchQuery, featureGroupId, featureId);
        setFeatureSearchResults(results);
      } catch (error) {
        logger.error('Error searching features:', error);
      }
    };

    const debounce = setTimeout(searchFeatures, 300);
    return () => clearTimeout(debounce);
  }, [featureSearchQuery, featureGroupId, featureId]);

  const isFeatureType = formData.type === 'Feature';

  const validateForm = () => {
    const newErrors = {};
    
    if (isFeatureType) {
      if (!selectedFeature) {
        newErrors.feature = 'Please select a feature to link';
      }
    } else {
      if (!formData.name.trim()) {
        newErrors.name = 'Name is required';
      }
      if (!formData.url.trim()) {
        newErrors.url = 'URL is required';
      } else {
        try {
          new URL(formData.url);
        } catch (e) {
          newErrors.url = 'Please enter a valid URL';
        }
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAdd = async () => {
    if (!validateForm()) return;

    try {
      if (isFeatureType) {
        // Create feature-to-feature link
        await createFeatureFeatureLink(featureId, selectedFeature.id, formData.featureLinkType);
      } else {
        // Create external link
        const sanitizedUrl = sanitizeURL(formData.url.trim());
        if (!sanitizedUrl) {
          setErrors({ url: 'Please enter a valid URL (http:// or https://)' });
          return;
        }

        await createFeatureLink({
          feature_id: featureId,
          name: sanitizeInput(formData.name.trim()),
          url: sanitizedUrl,
          type: formData.type
        });
      }
      
      resetForm();
      await loadAllLinks();
    } catch (error) {
      logger.error('Error creating link:', error);
      setErrors({ submit: handleError(error, 'Failed to create link. Please try again.') });
    }
  };

  const handleEdit = (link) => {
    setEditingLink(link.id);
    setFormData({
      type: link.type,
      name: link.name,
      url: link.url,
      targetFeatureId: '',
      featureLinkType: 'related_to'
    });
    setShowAddForm(false);
    setErrors({});
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;

    try {
      const sanitizedUrl = sanitizeURL(formData.url.trim());
      if (!sanitizedUrl) {
        setErrors({ url: 'Please enter a valid URL (http:// or https://)' });
        return;
      }

      await updateFeatureLink(editingLink, {
        name: sanitizeInput(formData.name.trim()),
        url: sanitizedUrl,
        type: formData.type
      });
      
      resetForm();
      await loadAllLinks();
    } catch (error) {
      logger.error('Error updating link:', error);
      setErrors({ submit: handleError(error, 'Failed to update link. Please try again.') });
    }
  };

  const handleDeleteExternal = async (linkId) => {
    if (!window.confirm('Are you sure you want to delete this link?')) {
      return;
    }

    try {
      await deleteFeatureLink(linkId);
      await loadAllLinks();
    } catch (error) {
      logger.error('Error deleting link:', error);
      setErrors({ submit: handleError(error, 'Failed to delete link. Please try again.') });
    }
  };

  const handleDeleteFeatureLink = async (linkId) => {
    if (!window.confirm('Are you sure you want to delete this feature link?')) {
      return;
    }

    try {
      await deleteFeatureFeatureLink(linkId);
      await loadAllLinks();
    } catch (error) {
      logger.error('Error deleting feature link:', error);
      setErrors({ submit: handleError(error, 'Failed to delete link. Please try again.') });
    }
  };

  const resetForm = () => {
    setEditingLink(null);
    setShowAddForm(false);
    setFormData({ 
      type: 'Internal Doc', 
      name: '', 
      url: '',
      targetFeatureId: '',
      featureLinkType: 'related_to'
    });
    setFeatureSearchQuery('');
    setFeatureSearchResults([]);
    setSelectedFeature(null);
    setShowFeatureDropdown(false);
    setErrors({});
  };

  const handleSelectFeature = (feature) => {
    setSelectedFeature(feature);
    setFeatureSearchQuery(feature.name);
    setShowFeatureDropdown(false);
    setFormData({ ...formData, targetFeatureId: feature.id });
  };

  const handleFeatureClick = (linkedFeatureId) => {
    navigate(`/feature/${linkedFeatureId}`);
  };

  const handleTypeChange = (newType) => {
    setFormData({ 
      ...formData, 
      type: newType,
      name: '',
      url: '',
      targetFeatureId: '',
      featureLinkType: 'related_to'
    });
    setSelectedFeature(null);
    setFeatureSearchQuery('');
    setErrors({});
  };

  if (loading) {
    return <div className="related-info-loading">Loading related information...</div>;
  }

  const allLinksEmpty = externalLinks.length === 0 && featureLinks.length === 0;

  return (
    <div className="related-information">
      <div className="related-info-header">
        <h3>Related Information</h3>
        {isEditMode && !showAddForm && !editingLink && (
          <button 
            type="button" 
            className="add-link-button"
            onClick={() => setShowAddForm(true)}
          >
            <i className="fas fa-plus"></i> Add Link
          </button>
        )}
      </div>

      {(showAddForm || editingLink) && isEditMode && (
        <div className="link-form">
          {/* Type selector - always first */}
          <div className="form-row">
            <div className="form-field">
              <label>
                Type <span className="required">*</span>
              </label>
              <select
                value={formData.type}
                onChange={(e) => handleTypeChange(e.target.value)}
                disabled={!!editingLink} // Can't change type when editing
              >
                {(editingLink ? EXTERNAL_LINK_TYPES : ALL_LINK_TYPES).map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Feature type - show feature search and link type */}
          {isFeatureType && !editingLink && (
            <>
              <div className="form-row">
                <div className="form-field">
                  <label>
                    Link Type <span className="required">*</span>
                  </label>
                  <select
                    value={formData.featureLinkType}
                    onChange={(e) => setFormData({ ...formData, featureLinkType: e.target.value })}
                  >
                    {FEATURE_LINK_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-field full-width feature-search-field">
                  <label>
                    Feature <span className="required">*</span>
                  </label>
                  <div className="feature-search-wrapper">
                    <input
                      type="text"
                      value={featureSearchQuery}
                      onChange={(e) => {
                        setFeatureSearchQuery(e.target.value);
                        setShowFeatureDropdown(true);
                        if (selectedFeature && e.target.value !== selectedFeature.name) {
                          setSelectedFeature(null);
                        }
                      }}
                      onFocus={() => setShowFeatureDropdown(true)}
                      onBlur={() => setTimeout(() => setShowFeatureDropdown(false), 200)}
                      className={errors.feature ? 'error' : ''}
                      placeholder="Search for a feature by name..."
                    />
                    {selectedFeature && (
                      <button
                        type="button"
                        className="clear-feature-button"
                        onClick={() => {
                          setSelectedFeature(null);
                          setFeatureSearchQuery('');
                        }}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                    {showFeatureDropdown && featureSearchResults.length > 0 && (
                      <div className="feature-search-dropdown">
                        {featureSearchResults.map(feature => (
                          <div
                            key={feature.id}
                            className="feature-search-option"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleSelectFeature(feature);
                            }}
                          >
                            <span className="feature-option-name">{feature.name}</span>
                            {feature.status && feature.status !== 'No Status' && (
                              <span className={`feature-option-status status-${feature.status.toLowerCase().replace(/\s+/g, '-')}`}>
                                {feature.status}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {showFeatureDropdown && featureSearchQuery.length >= 2 && featureSearchResults.length === 0 && (
                      <div className="feature-search-dropdown">
                        <div className="feature-search-no-results">No features found</div>
                      </div>
                    )}
                  </div>
                  {errors.feature && <span className="error-message">{errors.feature}</span>}
                </div>
              </div>
            </>
          )}

          {/* Non-Feature type - show name and URL */}
          {!isFeatureType && (
            <>
              <div className="form-row">
                <div className="form-field full-width">
                  <label>
                    Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={errors.name ? 'error' : ''}
                    placeholder="Link name"
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-field full-width">
                  <label>
                    URL <span className="required">*</span>
                  </label>
                  <input
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    className={errors.url ? 'error' : ''}
                    placeholder="https://example.com"
                  />
                  {errors.url && <span className="error-message">{errors.url}</span>}
                </div>
              </div>
            </>
          )}

          {errors.submit && <div className="error-message submit-error">{errors.submit}</div>}

          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-button"
              onClick={resetForm}
            >
              Cancel
            </button>
            <button 
              type="button" 
              className="save-button"
              onClick={editingLink ? handleUpdate : handleAdd}
            >
              {editingLink ? 'Update' : 'Add'} Link
            </button>
          </div>
        </div>
      )}

      {allLinksEmpty && !showAddForm && !editingLink && (
        <div className="no-links">
          {isEditMode ? 'No related information. Click "Add Link" to add one.' : 'No related information.'}
        </div>
      )}

      {/* Feature Links Section */}
      {featureLinks.length > 0 && (
        <div className="links-section">
          <h4 className="links-section-title">Feature Links</h4>
          <div className="links-list">
            {featureLinks.map(link => (
              <div key={`${link.id}-${link.direction}`} className="link-item feature-link-item">
                <div className="link-content">
                  <div className="link-name-type">
                    <span className="feature-link-type-label">
                      {getLinkTypeLabel(link.linkType)}
                    </span>
                    <button
                      type="button"
                      className="feature-link-name"
                      onClick={() => handleFeatureClick(link.linkedFeatureId)}
                    >
                      {link.linkedFeature?.name || 'Unknown Feature'}
                    </button>
                    <span className="link-type-badge type-feature">Feature</span>
                  </div>
                  {link.linkedFeature?.status && link.linkedFeature.status !== 'No Status' && (
                    <div className="feature-link-status">
                      <span className={`status-pill-small status-${link.linkedFeature.status.toLowerCase().replace(/\s+/g, '-')}`}>
                        {link.linkedFeature.status}
                      </span>
                    </div>
                  )}
                </div>
                {isEditMode && link.direction === 'outgoing' && (
                  <div className="link-actions">
                    <button 
                      type="button"
                      className="icon-button"
                      onClick={() => handleDeleteFeatureLink(link.id)}
                      title="Delete"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* External Links Section */}
      {externalLinks.length > 0 && (
        <div className="links-section">
          {featureLinks.length > 0 && <h4 className="links-section-title">External Links</h4>}
          <div className="links-list">
            {externalLinks.map(link => (
              <div key={link.id} className="link-item">
                {editingLink === link.id && isEditMode ? (
                  <div className="link-form-inline">
                    <div className="form-field">
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      >
                        {EXTERNAL_LINK_TYPES.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-field">
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className={errors.name ? 'error' : ''}
                        placeholder="Link name"
                      />
                    </div>
                    <div className="form-field">
                      <input
                        type="url"
                        value={formData.url}
                        onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                        className={errors.url ? 'error' : ''}
                        placeholder="URL"
                      />
                    </div>
                    <div className="link-actions">
                      <button 
                        type="button"
                        className="icon-button"
                        onClick={handleUpdate}
                        title="Save"
                      >
                        <i className="fas fa-check"></i>
                      </button>
                      <button 
                        type="button"
                        className="icon-button"
                        onClick={resetForm}
                        title="Cancel"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="link-content">
                      <div className="link-name-type">
                        <a 
                          href={sanitizeURL(link.url) || '#'} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="link-name"
                        >
                          {sanitizeInput(link.name)}
                        </a>
                        <span className={`link-type-badge type-${link.type.replace(/\s+/g, '-').toLowerCase()}`}>
                          {link.type}
                        </span>
                      </div>
                      <div className="link-url">
                        <a 
                          href={sanitizeURL(link.url) || '#'} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="link-url-text"
                        >
                          {sanitizeURL(link.url) || link.url}
                        </a>
                      </div>
                    </div>
                    {isEditMode && (
                      <div className="link-actions">
                        <button 
                          type="button"
                          className="icon-button"
                          onClick={() => handleEdit(link)}
                          title="Edit"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button 
                          type="button"
                          className="icon-button"
                          onClick={() => handleDeleteExternal(link.id)}
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RelatedInformation;
